import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

// Import your background image
import backgroundImage from '../assets/images/linewave.webp'; // Adjust path and filename as needed
// Import the second image for the right side
import rightSideImage from '../assets/images/formsdash.png'; // Replace with your image path and filename
// Import the new logo image
import sfLogo from '../assets/images/sflogo7.png'; // Make sure this path is correct!

// Define global keyframes for the animation
const GlobalStyle = createGlobalStyle`
  @keyframes moveWave {
    0% { background-position: 0% 0%; }
    100% { background-position: 100% 0%; }
  }

@keyframes spinFade {
  0% {
    transform: rotateY(0deg) scale(1); /* No rotation on Y-axis, normal size */
    opacity: 0.8;
  }
  50% {
    transform: rotateY(10deg) scale(1.02); /* Slight rotation and scale */
    opacity: 1;
  }
  100% {
    transform: rotateY(0deg) scale(1);
    opacity: 0.8;
  }
}

  /* Ensure body and html are not creating extra scrollbars or interfering with 100vh */
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    overflow-x: hidden; /* Prevent horizontal scroll for the entire page */
  }
`;

const DashboardContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  min-height: 100vh; /* Use min-height to allow content to push container height */
  width: 100%;
  position: relative;
  overflow-x: hidden; /* Prevent horizontal scroll on small screens */
  overflow-y: auto; /* Allow vertical scroll if content overflows */

  // *** FULL-PAGE ANIMATED BACKGROUND ***
  background-image: url(${backgroundImage});
  background-size: 200% 100%;
  background-position: 0% 0%;
  background-repeat: repeat-x;
  animation: moveWave 50s linear infinite;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.05);
    z-index: 0;
  }

  // Mobile adjustments
  @media (max-width: 768px) { /* Adjust breakpoint as needed for tablets/large phones */
    flex-direction: column; /* Stack content and image vertically */
    align-items: center; /* Center items horizontally when stacked */
    padding: 1rem; /* Add some padding around the whole container on mobile */
    min-height: auto; /* Allow height to adjust to content on mobile */
  }

  @media (max-width: 480px) { /* Smaller phones */
    padding: 0.5rem; /* Even less padding on very small screens */
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  max-width: 50%; /* Desktop default */
  padding: 40px; /* Use a fixed value or theme.spacing.xLarge if available */
  box-sizing: border-box;
  color: #2c3e50; /* Example dark blue color, replace with theme.colors.textDarkBlue */
  order: 1; /* Keep content on the left for desktop, and default top when stacked on mobile */

  // Mobile adjustments
  @media (max-width: 768px) {
    max-width: 100%; /* Take full width on mobile */
    padding: 20px; /* Reduce padding on mobile */
    padding-top: 40px; /* **ADDED/ADJUSTED:** Add more top padding on mobile */
    align-items: center; /* Center content elements on mobile */
    text-align: center; /* Center text within this wrapper */
    order: 1; /* Set content to appear first (above image) on mobile */
  }

  @media (max-width: 480px) {
    padding: 15px; /* Further reduce padding on smaller phones */
    padding-top: 560px; /* **ADDED/ADJUSTED:** More top padding for very small screens */
  }
`;

const LogoImage = styled.img`
  max-width: 500px;
  height: auto;
  margin-bottom: 32px; /* Use a fixed value or theme.spacing.large */
  align-self: flex-end; /* Desktop default: right alignment */
  animation: spinFade 2s ease-in-out infinite alternate;
  display: block; /* Ensure it behaves as a block element for centering */

  // Mobile adjustments
  @media (max-width: 768px) {
    max-width: 180px; /* Smaller logo on tablets */
    margin-bottom: 20px;
    align-self: center; /* Center logo on mobile */
    animation: spinFade 2s ease-in-out infinite alternate;; /* Disable complex animation on mobile for performance */
    filter: drop-shadow(0 0 10px rgba(229, 236, 238, 0.6)); /* Add a subtle shadow if animation is off */
    /* **ADDED:** Explicit margin-top if needed, although padding-top on wrapper should handle it */
    margin-top: 0; /* Ensure no extra top margin pushes it up */
  }

  @media (max-width: 480px) {
    max-width: 120px; /* Even smaller logo on phones */
    margin-bottom: 15px;
  }
`;

const Title = styled.h1`
  color: #2c3e50; /* Example dark blue, replace with theme.colors.textDarkBlue */
  margin-bottom: 24px; /* Use a fixed value or theme.spacing.large */
  text-align: left; /* Desktop default */
  font-size: 2.5rem; /* Default desktop font size */

  // Mobile adjustments
  @media (max-width: 768px) {
    text-align: center; /* Center title on mobile */
    font-size: 2rem; /* Smaller font size for tablets */
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem; /* Even smaller for phones */
    margin-bottom: 10px;
  }
`;

const ParagraphText = styled.p`
  margin-bottom: 16px; /* Use a fixed value or theme.spacing.medium */
  font-size: 1.1rem; /* Default desktop font size */
  line-height: 1.6;
  color: inherit;
  text-align: left; /* Desktop default */

  strong {
    color: #007bff; /* Example primary dark, replace with theme.colors.primaryDark */
  }

  // Mobile adjustments
  @media (max-width: 768px) {
    font-size: 1rem; /* Slightly smaller text on tablets */
    text-align: center; /* Center text on mobile */
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem; /* Even smaller text on phones */
    margin-bottom: 8px;
  }
`;

const RightImage = styled.div`
  position: relative;
  z-index: 1;
  background-image: url(${rightSideImage});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 50%; /* Desktop default */
  height: 100%; /* Fill height of parent */
  min-height: 400px; /* Ensure a minimum height on desktop */
  flex-grow: 1;
  order: 2; /* Keep image on the right for desktop */


  // Mobile adjustments
  @media (max-width: 768px) {
    width: 80%; /* Smaller width for image on tablets */
    height: 250px; /* Fixed height for image on tablets */
    margin-top: 20px; /* Space between text content and image */
    background-size: contain; /* Ensure it fits */
    display: block; /* Ensure it's visible */
    order: 2; /* Set image to appear second (under content) on mobile */
  }

  @media (max-width: 480px) {
    width: 90%; /* Take up more width on smaller phones */
    height: 180px; /* Even smaller fixed height for image on phones */
    margin-top: 15px;
  }
`;

const DashboardScreen = () => {
  useEffect(() => {
    const storedUserString = localStorage.getItem('user');
    console.log(storedUserString);
    // You might want to add a redirect here if user is not logged in, similar to your LoginPage
    // if (!storedUserString) {
    //   navigate('/login'); // Assuming you have `useNavigate` from react-router-dom
    // }
  }, []);

  return (
    <DashboardContainer>
      <GlobalStyle />
      <ContentWrapper> {/* Content will now appear first on mobile due to 'order: 1' */}
        {/* The logo image */}
        <LogoImage src={sfLogo} alt="Smart Forms Logo" />

        <Title>Welcome to Your Dashboard</Title>
        <ParagraphText>
          The Dashboard provides a centralized view of your form management activities.
          This application empowers you to upload JSON files, which are then converted into digital forms,
          streamlining your data collection process.
        </ParagraphText>
        <ParagraphText>
          You can efficiently save these forms and assign them to specific individuals for completion and submission.
          The platform also offers comprehensive form history tracking, allowing you to monitor submissions and review form data, organized by date.
        </ParagraphText>
        <ParagraphText>
          Beyond the core functionalities of uploading JSON-based forms, saving them securely, and assigning them for user completion,
          this Dashboard extends its capabilities to offer a more nuanced and efficient form management experience.
        </ParagraphText>
        <ParagraphText>
          Imagine the ability to categorize and tag your forms based on department, project, or urgency, enabling quicker retrieval and better organization.
          Furthermore, the platform could incorporate tracking of form status, providing immediate visibility into which forms are pending, in progress, or completed.
        </ParagraphText>
      </ContentWrapper>
      <RightImage /> {/* Image will now appear second on mobile due to 'order: 2' */}
    </DashboardContainer>
  );
};

export default DashboardScreen;